/**
 * Extract the backend error description from a rejected API call.
 *
 * Download endpoints request `responseType: "blob"`, so when the server
 * responds with a JSON error body ({success, errors: [{description}]}) the
 * rejection reaching the component is a Blob of that JSON, not a parsed
 * object. Plain-JSON rejections (400/417 pass the body through) are handled
 * too. Returns null when no description can be extracted so callers can
 * fall back to their placeholder message.
 */
export const extractApiErrorDescription = async (rejection) => {
  try {
    let payload = rejection;
    if (payload instanceof Blob) {
      payload = JSON.parse(await payload.text());
    }
    return payload?.errors?.[0]?.description || null;
  } catch {
    return null;
  }
};

// "Active filter with hidden rows" — a file-level problem, not a cell-level
// one, so it replaces the per-cell error list entirely.
export const ACTIVE_FILTER_HIDDEN_ROWS_ERROR_CODE = 9007;

// Cap on how many distinct problems we spell out; any extra collapse into an
// "…and N more" line so a file with many unrelated errors can't grow the toast
// without bound. Groups are sorted by affected-cell count first, so the cap
// drops the least impactful problems, never the biggest ones.
const MAX_REASON_GROUPS_SHOWN = 6;

// Backend cell errors may be pre-aggregated: one error per problem carrying a
// `count` of affected cells (plus roomtype/date ranges in the description)
// instead of one error per bad cell. A missing or invalid count is treated as
// a single cell so the old per-cell shape still sums correctly.
const errorCellCount = (error) =>
  Number.isFinite(error?.count) && error.count > 0 ? error.count : 1;

// Every backend description is self-locating — "… in sheet 'RP1' on dates: …"
// for value errors, "… in sheet 'RP1' at row 70, col 2" for invalid dates —
// so the bullet is the description verbatim and no separate location line is
// ever appended. Identical descriptions still collapse into one bullet whose
// count covers all of them.
const reasonLabelFor = (error) => {
  const description = error?.description?.trim();
  if (description) {
    return description;
  }
  if (error?.field) {
    return `Invalid '${error.field}'`;
  }
  return error?.error || "Invalid entry";
};

/**
 * Build the toast message for a failed ARI upload from the backend's `errors`
 * array. A code-9007 error (active filter with hidden rows) wins outright: only
 * its description is shown. Otherwise each bullet is one distinct description
 * (duplicates collapse). The heading totals the affected cells (summing each
 * error's `count`) and bullets are listed most-affected first, so the cap on
 * shown bullets never hides the most impactful problem.
 */
export const buildAriUploadErrorMessage = (errors) => {
  const filterError = errors.find(
    (error) => error.code === ACTIVE_FILTER_HIDDEN_ROWS_ERROR_CODE
  );
  if (filterError) {
    return filterError.description;
  }

  const groups = new Map();
  errors.forEach((error) => {
    const reason = reasonLabelFor(error);
    if (!groups.has(reason)) {
      groups.set(reason, []);
    }
    groups.get(reason).push(error);
  });

  const groupEntries = [...groups.entries()]
    .map(([reason, groupErrors]) => ({
      reason,
      cellCount: groupErrors.reduce(
        (sum, error) => sum + errorCellCount(error),
        0
      ),
    }))
    .sort((a, b) => b.cellCount - a.cellCount);

  const totalCells = groupEntries.reduce(
    (sum, group) => sum + group.cellCount,
    0
  );
  const heading =
    totalCells === 1
      ? "1 cell has a bad entry."
      : `${totalCells} cells have bad entries.`;

  const lines = groupEntries
    .slice(0, MAX_REASON_GROUPS_SHOWN)
    .map(({ reason }) => `• ${reason}`);

  const remaining = groupEntries.length - lines.length;
  if (remaining > 0) {
    lines.push(
      `• …and ${remaining} more ${remaining === 1 ? "problem" : "problems"}`
    );
  }

  return `${heading}\n${lines.join("\n")}`;
};
